import cn from 'clsx'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import { DesktopLayoutSizes } from './Common/utils'
import TripItemSummary from '../TripModal/TripItems/Summary'

import NestableClickable from 'components/Common/NestableClickable'
import { mediaQueryDown, mediaQueryOnly } from 'components/utils/breakpoint'
import { useAppDispatch } from 'hooks/reduxHooks'
import { tripItemsMaxWidth } from 'tripPlanner/config'
import { SectionIdContext } from 'tripPlanner/contexts/SectionIdProvider'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { setCurrentSectionId } from 'tripPlanner/reducers/actions'
import { TripItem } from 'tripPlanner/types/tripItem'
import { isLeBooking } from 'tripPlanner/utils'
import { cardItemHoverAnimation } from 'tripPlanner/utils/css'

const Card = styled(NestableClickable)`
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${(props) => props.theme.borderRadius.large};
  min-height: ${rem(75)};
  max-width: ${tripItemsMaxWidth};
  width: 100%;
  break-inside: avoid-page;

  ${cardItemHoverAnimation}

  &.le-booking {
    ${mediaQueryDown.tablet} {
      flex-direction: column;
      align-items: stretch;
    }

    ${mediaQueryOnly.desktop} {
      flex-direction: column;
      align-items: stretch;
    }

    ${DesktopLayoutSizes} {
      flex-direction: row;
    }
  }
`

interface Props {
  children: React.ReactNode
  item: TripItem
  nonInteractable?: boolean
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

function BaseItemCard({ children, item, nonInteractable, onClick }: Props) {
  const sectionId = useContext(SectionIdContext)
  const dispatch = useAppDispatch()
  const openModal = useOpenModal()
  const isLeBookingItem = isLeBooking(item)
  const handleOnClick = useCallback(
    (e) => {
      dispatch(setCurrentSectionId(sectionId))
      openModal(TripItemSummary, { item })
      onClick?.(e)
    },
    [dispatch, item, onClick, openModal, sectionId],
  )

  if (nonInteractable) {
    return (
      <Card
        data-testid="trip-base-item-card"
        as="div"
        className={cn({ 'le-booking': isLeBookingItem })}
      >
        {children}
      </Card>
    )
  }

  return (
    <Card
      data-testid="trip-base-item-card"
      onClick={handleOnClick}
      className={cn({ 'le-booking': isLeBookingItem })}
    >
      {children}
    </Card>
  )
}

export default BaseItemCard
